import { Avatar, Box, IconButton, Popover, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import styles from './auth.module.scss';
import { ReactNode, useState } from 'react';
import { DropdownIcon } from '~/assets/icons';
import { useTranslation } from 'react-i18next';
import FlagVi from '~/assets/images/flag-vi.png';
import FlagEn from '~/assets/images/flag-en.png';
import { LanguageEnum } from '~/utils/enum';

const cx = classNames.bind(styles);

type Props = {
  children: ReactNode;
};

const AuthLayout = (props: Props) => {
  const { t, i18n } = useTranslation(['admin/layout', 'admin/common']);

  const [anchorLang, setAnchorLang] = useState<null | HTMLElement>(null);

  const currentLang = i18n.language;
  const openLang = Boolean(anchorLang);
  const idLang = openLang ? 'popover-lang' : undefined;

  const handleOpenLang = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorLang(event.currentTarget);
  };

  const handleCloseLang = () => {
    setAnchorLang(null);
  };

  const handleToggleLanguage = () => {
    const newLang = i18n.language === LanguageEnum.VI ? LanguageEnum.EN : LanguageEnum.VI;
    i18n.changeLanguage(newLang);

    handleCloseLang();
  };

  return (
    <Box className={cx('w-full', 'h-full', 'flex', 'justify-center', 'items-center', 'auth-layout')}>
      <Box className='absolute top-20 right-28'>
        <Box className='flex items-center gap-3 cursor-pointer' onClick={handleOpenLang}>
          <Avatar className={cx('image')} alt={currentLang} src={currentLang === LanguageEnum.VI ? FlagVi : FlagEn} />
          <Typography className='text-[#161A1F] text-sm'>
            {currentLang === LanguageEnum.VI ? t(LanguageEnum.VI) : t(LanguageEnum.EN)}
          </Typography>
          <IconButton className='flex justify-center p-0'>
            <DropdownIcon
              stroke={'#161A1F'}
              className={
                'w-4 h-4' +
                cx('transition-transform duration-300', {
                  'rotate-180': openLang,
                })
              }
            />
          </IconButton>
        </Box>
      </Box>
      <Popover
        id={idLang}
        open={openLang}
        anchorEl={anchorLang}
        onClose={handleCloseLang}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: '5px',
              borderRadius: '12px',
              padding: '10px',
            },
          },
        }}
      >
        <Box className='flex items-center gap-2 cursor-pointer' onClick={handleToggleLanguage}>
          {currentLang === LanguageEnum.VI ? (
            <>
              <Avatar className={cx('image')} alt={LanguageEnum.EN} src={FlagEn} />
              <Box className='justify-between hidden text-right sm:flex items-center'>
                <Box className='text-[#161A1F] text-sm'>{t(LanguageEnum.EN)}</Box>
              </Box>
            </>
          ) : (
            <>
              <Avatar className={cx('image')} alt={LanguageEnum.VI} src={FlagVi} />
              <Box className='justify-between hidden text-right sm:flex items-center'>
                <Box className='text-[#161A1F] text-sm'>{t(LanguageEnum.VI)}</Box>
              </Box>
            </>
          )}
        </Box>
      </Popover>
      <Box className='shadow-md m-auto bg-white rounded-[16px] w-[480px] p-6'>{props.children}</Box>
    </Box>
  );
};

export default AuthLayout;
